import React from 'react';

/**
 * ErrorBoundary — Root-level crash containment for KOMPA 2.0.
 *
 * Catches any unhandled JavaScript error that propagates through the
 * React component tree (render-phase throws, event handlers that
 * rethrow, async errors inside useEffect if they reach setState on
 * an unmounted component, etc.).
 *
 * Instead of a White Screen of Death, the user sees a branded recovery
 * view with a one-tap "Limpiar sesión" button that clears localStorage
 * and reloads the app — resolving the most common WSOD causes:
 *   • Stale/corrupt tokens in localStorage
 *   • Cached state that references removed API fields
 *   • Malformed JSON from incomplete writes
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Best-effort telemetry — log to console; could be wired to
    // PostHog / Sentry in the future.
    try {
      // eslint-disable-next-line no-console
      console.error('[KOMPA ErrorBoundary]', error, info?.componentStack);
    } catch {
      // ignore
    }
  }

  handleClearAndReload = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      // Storage might be disabled (private browsing + full quota)
    }
    window.location.replace('/');
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          backgroundColor: '#ffffff',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
        data-testid="error-boundary-fallback"
      >
        {/* Logo mark */}
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 16,
            backgroundColor: '#fbb040',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}
        >
          <span style={{ fontSize: 28, fontWeight: 900, color: '#1f2d5a' }}>K</span>
        </div>

        <h1
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: '#1f2d5a',
            marginBottom: 8,
            textAlign: 'center',
          }}
        >
          Algo no salió bien
        </h1>

        <p
          style={{
            fontSize: 14,
            color: '#6b7280',
            textAlign: 'center',
            maxWidth: 320,
            lineHeight: 1.5,
            marginBottom: 32,
          }}
        >
          La aplicación encontró un error inesperado.
          Puedes intentar recargar o limpiar la sesión para resolverlo.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 280 }}>
          <button
            onClick={this.handleReload}
            data-testid="error-boundary-reload"
            style={{
              width: '100%',
              height: 48,
              borderRadius: 9999,
              backgroundColor: '#fbb040',
              color: '#000000',
              fontWeight: 700,
              fontSize: 15,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Recargar página
          </button>
          <button
            onClick={this.handleClearAndReload}
            data-testid="error-boundary-clear-session"
            style={{
              width: '100%',
              height: 48,
              borderRadius: 9999,
              backgroundColor: '#1f2d5a',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: 15,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Limpiar sesión y reiniciar
          </button>
        </div>

        {/* Error detail for debugging (dev-only or power users) */}
        <details
          style={{
            marginTop: 40,
            maxWidth: 340,
            width: '100%',
            fontSize: 11,
            color: '#9ca3af',
          }}
        >
          <summary style={{ cursor: 'pointer', marginBottom: 6 }}>Detalle técnico</summary>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              backgroundColor: '#f9fafb',
              padding: 12,
              borderRadius: 8,
              maxHeight: 120,
              overflow: 'auto',
            }}
          >
            {this.state.error?.toString?.() || 'Error desconocido'}
          </pre>
        </details>
      </div>
    );
  }
}
